"use client";

import { useState } from "react";
import Link from "next/link";
import type { CtaLink, NavItem } from "@/lib/content";
import { Wordmark } from "./Logo";
import { Container } from "./Container";

export function SiteHeader({
  nav,
  cta,
}: {
  nav: NavItem[];
  cta: CtaLink;
}) {
  const [open, setOpen] = useState(false);

  return (
    <header className="sticky top-0 z-50 border-b border-line bg-[#f6f5f2]/80 backdrop-blur-md backdrop-saturate-150">
      <Container className="flex h-[66px] items-center justify-between">
        <Link href="/" aria-label="Fulcrum Foundation home">
          <Wordmark />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden items-center gap-7 md:flex">
          {nav.map((item) => (
            <NavLink key={item.label} item={item} />
          ))}
          <Link
            href={cta.href}
            className="rounded-sm bg-ink px-[17px] py-[9px] font-sans text-[14px] font-semibold text-white transition-colors hover:bg-[#0c1521]"
          >
            {cta.label}
          </Link>
        </nav>

        {/* Mobile toggle */}
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
          aria-expanded={open}
          className="flex h-10 w-10 items-center justify-center rounded-sm border border-line-strong md:hidden"
        >
          <svg width="18" height="14" viewBox="0 0 18 14" aria-hidden="true">
            <path d="M0 1h18M0 7h18M0 13h18" stroke="#16202E" strokeWidth="1.6" />
          </svg>
        </button>
      </Container>

      {/* Mobile menu */}
      {open && (
        <nav className="border-t border-line bg-paper px-7 py-5 md:hidden">
          <ul className="flex flex-col gap-1">
            {nav.map((item) => (
              <li key={item.label} className="py-1.5">
                <Link
                  href={item.href}
                  onClick={() => setOpen(false)}
                  className="font-display text-lg font-semibold"
                >
                  {item.label}
                </Link>
                {item.children && (
                  <ul className="mt-1.5 flex flex-col gap-1 border-l border-line pl-4">
                    {item.children.map((c, ci) => (
                      <li key={`${c.href}-${ci}`}>
                        <Link
                          href={c.href}
                          onClick={() => setOpen(false)}
                          className="text-[15px] text-ink-60"
                        >
                          {c.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
            ))}
            <li className="mt-3">
              <Link
                href={cta.href}
                onClick={() => setOpen(false)}
                className="inline-block rounded-sm bg-ink px-[17px] py-2.5 text-[14px] font-semibold text-white"
              >
                {cta.label}
              </Link>
            </li>
          </ul>
        </nav>
      )}
    </header>
  );
}

function NavLink({ item }: { item: NavItem }) {
  if (!item.children) {
    return (
      <Link
        href={item.href}
        className="text-[14.5px] font-medium text-ink-60 transition-colors hover:text-ink"
      >
        {item.label}
      </Link>
    );
  }
  // Hover dropdown (CSS group). Keyboard users get the folder's own href.
  return (
    <div className="group relative">
      <Link
        href={item.href}
        className="flex items-center gap-1 text-[14.5px] font-medium text-ink-60 transition-colors hover:text-ink"
      >
        {item.label}
        <svg width="9" height="6" viewBox="0 0 9 6" aria-hidden="true" className="mt-0.5">
          <path d="M1 1l3.5 3.5L8 1" stroke="currentColor" strokeWidth="1.3" fill="none" />
        </svg>
      </Link>
      <div className="invisible absolute left-0 top-full z-50 min-w-[260px] pt-3 opacity-0 transition-all group-hover:visible group-hover:opacity-100">
        <ul className="rounded-sm border border-line bg-card py-2 shadow-[0_12px_40px_-16px_rgba(22,32,46,0.35)]">
          {item.children.map((c, ci) => (
            <li key={`${c.href}-${ci}`}>
              <Link
                href={c.href}
                className="block px-4 py-2 text-[14px] text-ink-60 transition-colors hover:bg-paper-2 hover:text-ink"
              >
                {c.label}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
